import React, { useState, useEffect } from "react";
import { Nav } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import {
  FaTachometerAlt,
  FaUsers,
  FaUserCog,
  FaUserTie,
  FaBoxes,
  FaFileInvoice,
  FaMoneyBillWave,
  FaSignOutAlt,
  FaUserCircle,
  FaBuilding,
  FaUserShield,
  FaList,
  FaFileContract,
  FaDatabase,
  FaMapMarkerAlt,
  FaGasPump,
  FaTags,
  FaWallet,
  FaChartLine,
  FaHistory,
  FaFileInvoiceDollar,
  FaChartBar,
  FaUserClock,
  FaHeartbeat,
  FaCogs,
  FaSlidersH,
  FaEnvelope,
  FaPlug,
  FaShieldAlt,
  FaClipboardList,
  FaUserLock,
  FaTools,
  FaRedoAlt,
  FaWrench,
  FaGlobe,
  FaCity,
  FaMailBulk,
  FaTrademark,
  FaCubes,
  FaMoneyCheckAlt,
  FaStar,
  FaUserFriends,
  FaCreditCard,
  FaFileAlt,
  FaFolderOpen,
  FaCrown,
  FaLayerGroup,
  FaShoppingCart,
  FaReceipt,
  FaFilePdf,
  FaDownload,
  FaPlusCircle,
  FaEdit,
  FaEye,
  FaGift,
  FaCheckCircle,
  FaAward,
  FaTrophy,
  FaUser,
  FaClipboardCheck,
  FaTruck,
  FaRoute,
  FaClock,
  FaCalendarAlt,
  FaBan,
  FaExchangeAlt,
  FaExclamationTriangle,
  FaBook,
  FaBell,
  FaBullhorn,
  FaIdCard,
  FaSearch,
  FaCloud,
  FaLock,
} from "react-icons/fa";

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [openMenu, setOpenMenu] = useState({});

  const toggleMenu = (key) => {
    setOpenMenu((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // ✅ Get role
  const role = localStorage.getItem("role");

  // ✅ Check if any child path is active (recursive)
  const isChildActive = (children) => {
    if (!children) return false;

    for (let child of children) {
      if (child.path && location.pathname === child.path) {
        return true;
      }
      if (child.children && isChildActive(child.children)) {
        return true;
      }
    }
    return false;
  };

  // ✅ Auto open dropdowns when inside any child route
  useEffect(() => {
    if (role === "superadmin") {
      const newOpenState = {};

      const checkAndOpen = (items, parentKey = "") => {
        items.forEach((item, index) => {
          const key = parentKey ? `${parentKey}-${index}` : `${index}`;

          if (item.children && isChildActive(item.children)) {
            newOpenState[key] = true;
            checkAndOpen(item.children, key);
          }
        });
      };

      checkAndOpen(superAdminMenu);
      setOpenMenu((prev) => ({ ...prev, ...newOpenState }));
    } else if (role === "admin") {
      const newOpenState = {};

      const checkAndOpen = (items, parentKey = "") => {
        items.forEach((item, index) => {
          const key = parentKey ? `${parentKey}-${index}` : `${index}`;

          if (item.children && isChildActive(item.children)) {
            newOpenState[key] = true;
            checkAndOpen(item.children, key);
          }
        });
      };

      checkAndOpen(adminMenu);
      setOpenMenu((prev) => ({ ...prev, ...newOpenState }));
    }
  }, [location.pathname, role]);

  // ✅ Recursive component to render menu items with any level of nesting
  const renderMenuItem = (item, index, parentKey = "") => {
    const hasChildren = item.children && item.children.length > 0;
    const isActive = item.path ? location.pathname === item.path : false;
    const isParentActive = hasChildren && isChildActive(item.children);

    const menuKey = parentKey ? `${parentKey}-${index}` : `${index}`;
    const isOpen = openMenu[menuKey] || false;

    // For leaf nodes (no children)
    if (!hasChildren) {
      return (
        <Nav.Link
          key={menuKey}
          as={Link}
          to={item.path}
          className={`d-flex align-items-center gap-2 ${location.pathname === item.path ? "active" : ""
            }`}
          style={{
            padding:
              parentKey.split("-").length === 2 ? "6px 10px" : "8px 12px",
            fontSize: parentKey.split("-").length === 2 ? "0.85rem" : "0.9rem",
            marginLeft: parentKey.split("-").length === 2 ? "0" : "0",
          }}
        >
          {item.icon && <span style={{ minWidth: "1.2rem" }}>{item.icon}</span>}
          <span>{item.name}</span>
        </Nav.Link>
      );
    }

    // For parent nodes (with children)
    return (
      <div key={menuKey}>
        <div
          className={`d-flex justify-content-between align-items-center sidebar-item ${isParentActive ? "parent-active" : ""
            }`}
          onClick={() => toggleMenu(menuKey)}
          style={{
            cursor: "pointer",
            padding: parentKey.split("-").length === 2 ? "8px 12px" : "10px",
            fontSize: parentKey.split("-").length === 2 ? "0.85rem" : "0.9rem",
          }}
        >
          <div className="d-flex align-items-center gap-2">
            {item.icon && (
              <span style={{ minWidth: "1.2rem" }}>{item.icon}</span>
            )}
            <span>{item.name}</span>
          </div>
          <span style={{ fontSize: "0.7rem" }}>{isOpen ? "▲" : "▼"}</span>
        </div>

        {isOpen && (
          <div className={parentKey.split("-").length === 2 ? "ms-4" : "ms-3"}>
            {item.children.map((child, childIndex) =>
              renderMenuItem(child, childIndex, menuKey),
            )}
          </div>
        )}
      </div>
    );
  };

  // ✅ Admin Menu - ALL EXISTING PATHS PRESERVED, new structure added
  const adminMenu = [
    // Dashboard - KEPT SAME
    { path: "/dashboard", name: "Dashboard", icon: <FaTachometerAlt /> },

    // Customer & Connections - NEW GROUP (with new paths)
    {
      name: "Customer & Connections",
      icon: <FaUsers />,
      children: [
        { path: "/customers", name: "Manage Customers", icon: <FaUsers /> },
        { path: "/dealers", name: "Manage Dealers", icon: <FaUserTie /> },
        { path: "/connections", name: "Manage Connections", icon: <FaPlug /> },
        // {
        //   name: "Customers",
        //   icon: <FaUserFriends />,
        //   children: [
        //     { path: "/customers", name: "Manage Customers", icon: <FaList /> },
        //     { path: "/dealers", name: "Manage Dealers", icon: <FaUserTie /> },

        //     // { path: "/customers/kyc", name: "KYC Verification", icon: <FaClipboardCheck /> },
        //     // { path: "/customers/consumer-numbers", name: "Consumer Numbers", icon: <FaIdCard /> },
        //     // { path: "/customers/transfer", name: "Transfer", icon: <FaExchangeAlt /> },
        //     // { path: "/customers/suspend", name: "Suspend", icon: <FaBan /> },
        //   ],
        // },
        // {
        //   name: "Connections",
        //   icon: <FaPlug />,
        //   children: [
        //     { path: "/connections", name: "Manage Connections", icon: <FaList /> },
        //     // { path: "/connections/new", name: "New Connection", icon: <FaPlusCircle /> },
        //     // { path: "/connections/transfer", name: "Connection Transfer", icon: <FaExchangeAlt /> },
        //     // { path: "/connections/status", name: "Connection Status", icon: <FaClipboardCheck /> },
        //   ],
        // },
      ],
    },

    // Bookings & Delivery - NEW GROUP (with new paths)
    {
      name: "Bookings & Delivery",
      icon: <FaShoppingCart />,
      children: [
        { path: "/bookings", name: "Manage Bookings", icon: <FaBook /> },
        // { path: "/delivery/assignment", name: "Delivery Manage", icon: <FaTruck /> },
        // {
        //   name: "Bookings",
        //   icon: <FaBook />,
        //   children: [
        //     { path: "/bookings", name: "Manage Bookings", icon: <FaList /> },
        //     // { path: "/bookings/new", name: "New Booking", icon: <FaPlusCircle /> },
        //     // { path: "/bookings/sources", name: "Booking Sources", icon: <FaSearch /> },
        //     // { path: "/bookings/cancelled", name: "Cancelled Bookings", icon: <FaBan /> },
        //   ],
        // },
        // {
        //   name: "Delivery",
        //   icon: <FaTruck />,
        //   children: [
        //     { path: "/delivery/assignment", name: "Delivery Assignment", icon: <FaUserClock /> },
        //     { path: "/delivery/status", name: "Delivery Status", icon: <FaClipboardCheck /> },
        //     { path: "/delivery/boys", name: "Delivery Boys", icon: <FaUserTie /> },
        //     { path: "/delivery/vehicles", name: "Vehicles", icon: <FaTruck /> },
        //     { path: "/delivery/routes", name: "Routes", icon: <FaRoute /> },
        //   ],
        // },
      ],
    },

    // Cylinder Inventory - NEW GROUP (with new paths)
    {
      name: "Cylinder Inventory",
      icon: <FaBoxes />,
      children: [
        {
          name: "Stock Types",
          icon: <FaLayerGroup />,
          children: [
            { path: "/inventory", name: "Manage Inventory", icon: <FaCheckCircle /> },
            { path: "/inventory/empty", name: "Empty Cylinders", icon: <FaBoxes /> },
            { path: "/inventory/damaged", name: "Damaged Cylinders", icon: <FaExclamationTriangle /> },
            { path: "/inventory/reserved", name: "Reserved Cylinders", icon: <FaLock /> },
          ],
        },
        {
          name: "Operations",
          icon: <FaCogs />,
          children: [
            { path: "/inventory/management", name: "Stock Management", icon: <FaTools /> },
            { path: "/inventory/transactions", name: "Stock Transactions", icon: <FaHistory /> },
            { path: "/inventory/alerts", name: "Stock Alerts", icon: <FaBell /> },
            { path: "/inventory/report", name: "Stock Report", icon: <FaFileInvoice /> },
          ],
        },
      ],
    },

    // Finance - NEW GROUP with submenus (KEEPING existing standalone paths too)
    {
      name: "Finance",
      icon: <FaWallet />,
      children: [
        { path: "/expenses", name: "Manage Expenses", icon: <FaMoneyBillWave /> },
        { path: "/invoices", name: "Manage Invoices", icon: <FaFileInvoice /> },


        // {
        //   name: "Invoices",
        //   icon: <FaFileInvoice />,
        //   children: [
        //     { path: "/invoices", name: "All Invoices", icon: <FaList /> },
        //     // { path: "/invoices/create", name: "Create Invoice", icon: <FaPlusCircle /> },
        //     // { path: "/invoices/pending", name: "Pending Invoices", icon: <FaClock /> },
        //     // { path: "/invoices/credit-notes", name: "Credit Notes", icon: <FaFileInvoiceDollar /> },
        //   ],
        // },
        // {
        //   name: "Payments",
        //   icon: <FaCreditCard />,
        //   children: [
        //     { path: "/payments/all", name: "All Payments", icon: <FaList /> },
        //     { path: "/payments/outstanding", name: "Outstanding Payments", icon: <FaExclamationTriangle /> },
        //     { path: "/payments/collection", name: "Payment Collection", icon: <FaMoneyBillWave /> },
        //     { path: "/payments/online", name: "Online Payments", icon: <FaCloud /> },
        //   ],
        // },
        // {
        //   name: "Expenses",
        //   icon: <FaMoneyBillWave />,
        //   children: [
        //     { path: "/expenses", name: "All Expenses", icon: <FaList /> },
        //     // { path: "/expenses/categories", name: "Expense Categories", icon: <FaTags /> },
        //     // { path: "/expenses/reports", name: "Expense Reports", icon: <FaChartBar /> },
        //   ],
        // },
      ],
    },

    // Reports - UPDATED with submenus (KEEPING /reports path)
    {
      name: "Reports",
      icon: <FaChartLine />,
      children: [
        { path: "/reports", name: "All Reports", icon: <FaChartBar /> }, // KEPT SAME
        { path: "/reports/sales", name: "Sales Report", icon: <FaChartBar /> },
        // { path: "/reports/stock", name: "Stock Report", icon: <FaBoxes /> },
        // { path: "/reports/delivery", name: "Delivery Report", icon: <FaTruck /> },
        { path: "/reports/customer", name: "Customer Report", icon: <FaUsers /> },
        { path: "/reports/financial", name: "Financial Report", icon: <FaFileInvoiceDollar /> },
        // { path: "/reports/staff", name: "Staff Report", icon: <FaUserFriends /> },
      ],
    },

    // Administration - NEW GROUP (KEEPING existing staff and company-setting paths)
    {
      name: "Administration",
      icon: <FaUserCog />,
      children: [
        {
          name: "Staff",
          icon: <FaUserFriends />,
          children: [
            { path: "/staffs", name: "Staff Management", icon: <FaUsers /> }, // KEPT SAME
            // { path: "/staff/attendance", name: "Attendance", icon: <FaClock /> },
            { path: "/staff/payroll", name: "Payroll", icon: <FaMoneyBillWave /> }, // KEPT SAME
            { path: "/staff/bonuses", name: "Bonuses", icon: <FaGift /> }, // KEPT SAME
            // { path: "/staff/targets", name: "Targets", icon: <FaBullhorn /> },
            // { path: "/staff/leave", name: "Leave Management", icon: <FaCalendarAlt /> },
          ],
        },
        {
          name: "Settings",
          icon: <FaCogs />,
          children: [
            { path: "/company-setting", name: "Company Settings", icon: <FaBuilding /> }, // KEPT SAME
            // { path: "/settings/general", name: "General Settings", icon: <FaSlidersH /> },
            // { path: "/settings/email", name: "Email Configuration", icon: <FaEnvelope /> },
            // { path: "/settings/backup", name: "Backup Settings", icon: <FaDatabase /> },
            // { path: "/settings/audit", name: "Audit Logs", icon: <FaClipboardList /> },
          ],
        },
      ],
    },

    // KEEPING EXISTING STANDALONE PATHS (these will also appear as leaf nodes)
    // Note: These are already included in the submenus above, but keeping them as standalone 
    // would cause duplication. Removing them to avoid confusion.
  ];

  // ✅ Super Admin Menu - ALL EXISTING PATHS PRESERVED
  const superAdminMenu = [
    {
      path: "/dashboard",
      name: "Dashboard",
      icon: <FaTachometerAlt />,
    },
    // Masters / Data Management
    {
      name: "Master Data",
      icon: <FaDatabase />,
      children: [
        {
          name: "Location Management",
          icon: <FaMapMarkerAlt />,
          children: [
            {
              path: "/superadmin/countries",
              name: "Countries",
              icon: <FaGlobe />,
            },
            {
              path: "/superadmin/states",
              name: "States",
              icon: <FaMapMarkerAlt />,
            },
            { path: "/superadmin/cities", name: "Cities", icon: <FaCity /> },
            {
              path: "/superadmin/pincodes",
              name: "Pincodes",
              icon: <FaMailBulk />,
            },
          ],
        },
        {
          name: "LPG Management",
          icon: <FaGasPump />,
          children: [
            {
              path: "/superadmin/lpg-brands",
              name: "Brands",
              icon: <FaTrademark />,
            },
            {
              path: "/superadmin/cylinder-types",
              name: "Cylinder Types",
              icon: <FaCubes />,
            },
            {
              path: "/superadmin/cylinder-rates",
              name: "Cylinder Rates",
              icon: <FaMoneyCheckAlt />,
            },
          ],
        },
        {
          name: "Plans & Pricing",
          icon: <FaTags />,
          children: [
            {
              path: "/superadmin/subscription-plans",
              name: "Subscription Plans",
              icon: <FaFileContract />,
            },
            {
              path: "/superadmin/plan-features",
              name: "Plan Features",
              icon: <FaStar />,
            },
          ],
        },
      ],
    },
    // Tenant Management
    {
      name: "Tenant Management",
      icon: <FaBuilding />,
      children: [
        {
          path: "/superadmin/tenants",
          name: "Manage Tenants",
          icon: <FaList />,
        },
        {
          path: "/superadmin/tenant-subscriptions",
          name: "Tenant Subscriptions",
          icon: <FaFileContract />,
        },
      ],
    },
    // User Management
    {
      name: "User Management",
      icon: <FaUserCog />,
      children: [
        { path: "/superadmin/User", name: "Manage Users", icon: <FaUsers /> },
        {
          path: "/superadmin/user-roles",
          name: "User Role",
          icon: <FaUserShield />,
        },
      ],
    },
    // Financial Management
    {
      name: "Financial Management",
      icon: <FaWallet />,
      children: [
        {
          path: "/superadmin/revenue-reports",
          name: "Revenue Reports",
          icon: <FaChartLine />,
        },
        {
          path: "/superadmin/payment-history",
          name: "Payment History",
          icon: <FaHistory />,
        },
        {
          path: "/superadmin/invoice-overview",
          name: "Invoice Overview",
          icon: <FaFileInvoiceDollar />,
        },
      ],
    },
    // Reports & Analytics
    {
      name: "Reports & Analytics",
      icon: <FaChartBar />,
      children: [
        {
          path: "/superadmin/tenant-growth",
          name: "Tenant Growth",
          icon: <FaUsers />,
        },
        {
          path: "/superadmin/revenue-analytics",
          name: "Revenue Analytics",
          icon: <FaChartLine />,
        },
        {
          path: "/superadmin/user-activity",
          name: "User Activity",
          icon: <FaUserClock />,
        },
        {
          path: "/superadmin/system-health",
          name: "System Health",
          icon: <FaHeartbeat />,
        },
      ],
    },
    // System Settings
    {
      name: "System Settings",
      icon: <FaCogs />,
      children: [
        {
          path: "/superadmin/general-settings",
          name: "General Settings",
          icon: <FaSlidersH />,
        },
        {
          path: "/superadmin/email-configuration",
          name: "Email Configuration",
          icon: <FaEnvelope />,
        },
        {
          path: "/superadmin/backup-settings",
          name: "Backup Settings",
          icon: <FaDatabase />,
        },
        {
          path: "/superadmin/api-settings",
          name: "API Settings",
          icon: <FaPlug />,
        },
      ],
    },
    // Security
    {
      name: "Security",
      icon: <FaShieldAlt />,
      children: [
        {
          path: "/superadmin/audit-logs",
          name: "Audit Logs",
          icon: <FaClipboardList />,
        },
        {
          path: "/superadmin/login-history",
          name: "Login History",
          icon: <FaHistory />,
        },
        {
          path: "/superadmin/permission-management",
          name: "Permission Management",
          icon: <FaUserLock />,
        },
      ],
    },
    // Backup & Maintenance
    {
      name: "Backup & Maintenance",
      icon: <FaTools />,
      children: [
        {
          path: "/superadmin/database-backup",
          name: "Database Backup",
          icon: <FaDatabase />,
        },
        {
          path: "/superadmin/restore-backup",
          name: "Restore Backup",
          icon: <FaRedoAlt />,
        },
        {
          path: "/superadmin/system-maintenance",
          name: "System Maintenance",
          icon: <FaWrench />,
        },
      ],
    },
  ];

  const menuItems = role === "superadmin" ? superAdminMenu : adminMenu;

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will be logged out of your account!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, logout!",
      cancelButtonText: "Cancel",
      background: "#fff",
      backdrop: true,
      allowOutsideClick: false,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const loadingToast = toast.loading("Logging out...", {
            position: "top-right",
            theme: "colored",
          });

          setTimeout(() => {
            localStorage.removeItem("isAuthenticated");
            localStorage.removeItem("role");
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            localStorage.removeItem("userType");
            localStorage.removeItem("subdomain");
            localStorage.removeItem("tenant");

            toast.update(loadingToast, {
              render: "Logged out successfully!",
              type: "success",
              isLoading: false,
              autoClose: 2000,
              transition: Bounce,
            });

            setTimeout(() => {
              navigate("/login");
            }, 1500);
          }, 1000);
        } catch (error) {
          console.error("Logout error:", error);
          toast.error("Failed to logout. Please try again.", {
            position: "top-right",
            autoClose: 3000,
            theme: "colored",
            transition: Bounce,
          });
        }
      }
    });
  };

  return (
    <div className="sidebar d-flex flex-column">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        transition={Bounce}
      />

      <div className="sidebar-header text-center">
        <div className="logo-container">
          <img
            src="/BILLING BRUDHI LOGO.svg"
            alt="Brudhi Logo"
            className="sidebar-logo"
            style={{
              width: "120px",
              height: "auto",
              marginBottom: "4px",
              border: "2px solid #fff",
              borderRadius: "8px",
              padding: "5px",
              backgroundColor: "#fff",
            }}
          />
        </div>
        <p style={{ fontSize: "0.75rem", opacity: 0.7, marginBottom: 0 }}>
          {role === "superadmin" ? "System Control Panel" : ""}
        </p>
      </div>

      <Nav className="flex-column px-3 mt-3">
        {menuItems.map((item, index) => renderMenuItem(item, index))}
      </Nav>

      <div className="mt-auto">
        <Nav.Link
          onClick={handleLogout}
          className="logout-btn d-flex align-items-center mx-3 mb-3"
        >
          <FaSignOutAlt className="me-2" />
          <span>Sign Out</span>
        </Nav.Link>
      </div>
    </div>
  );
};

export default Sidebar;